import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Bounce, toast } from 'react-toastify';
import { Modal, ModalBody } from 'reactstrap';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import ListingTabs from '../../components/Listing/ListingTabs';
import CustomButton from '../../components/OnboardingComponents/CustomButton';
import CustomInput from '../../components/OnboardingComponents/CustomInput';
import PasswordRequirementsHint from '../../components/OnboardingComponents/PasswordRequirementsHint';
import PreferredCitySelect from '../../components/Common/PreferredCitySelect';
import { ICONS } from '../../constants/icons';
import { get, patch, post } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import { setUser } from '../../store/slices/authSlice';
import {
  getActiveSessionSnapshot,
  markUnloadHandled,
  pauseActiveSession,
} from '../../helpers/activeSession';
import { performAppLogout } from '../../helpers/sessionLogout';
import { TIMELOG_REASONS } from '../../helpers/sessionDetail';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { formatDisplayName } from '../../helpers/userDisplay';
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
  getPasswordHelperTone,
} from '../../helpers/passwordRules';
import {
  formatCityName,
  getAddressByType,
} from './profileUser';
import ProfilePhoto from './ProfilePhoto';
import NotificationsTab from '../Admin/Settings/NotificationsTab';
import {
  buildDefaultNotificationPrefs,
  mapNotificationSettingsFromApi,
  mapNotificationSettingsToApi,
  NOTIFICATION_SECTIONS,
} from '../Admin/Settings/adminSettingsData';

const PROFILE_FORM_ID = 'edit-profile-form';
const MAX_PHOTO_BYTES = 5120 * 1024;
const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

const isAcceptedPhoto = file => {
  const mime = String(file?.type || '').toLowerCase();
  if (ACCEPTED_PHOTO_TYPES.includes(mime)) return true;
  const name = String(file?.name || '').toLowerCase();
  return /\.(jpe?g|png|gif)$/.test(name);
};

const EDIT_PROFILE_TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'notifications', label: 'Notifications' },
];

const ADDRESS_SLOTS = [
  { key: 'primary', field: 'primaryCityId', label: 'Primary City' },
  { key: 'secondary', field: 'secondaryCityId', label: 'Secondary City' },
  { key: 'tertiary', field: 'tertiaryCityId', label: 'Tertiary City' },
];

const TOAST_OPTIONS = {
  position: 'bottom-center',
  theme: 'colored',
  transition: Bounce,
};

const resolveAddressCityId = address =>
  address?.city?.id || address?.city_id || '';

const formatCityOptionLabel = city => {
  if (!city) return '';
  if (typeof city === 'string') return city.trim();
  const name = city.city || city.name || '';
  const state = city.state || '';
  if (name && state) return `${name}, ${state}`;
  return name || '';
};

/** Other address slots already using a city id (exclude from this slot’s menu). */
const getExcludeCityIdsForSlot = (values, field) =>
  ADDRESS_SLOTS.filter(slot => slot.field !== field)
    .map(slot => String(values?.[slot.field] || "").trim())
    .filter(Boolean)

const mapUserToForm = (user = {}) => {
  const addresses = user.addresses || [];
  return {
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    email: user.email || '',
    phone: user.phone || user.phone_number || '',
    listeningTo: user.listening_to || '',
    reading: user.reading || '',
    primaryCityId: resolveAddressCityId(getAddressByType(addresses, 'primary')),
    secondaryCityId: resolveAddressCityId(
      getAddressByType(addresses, 'secondary')
    ),
    tertiaryCityId: resolveAddressCityId(
      getAddressByType(addresses, 'tertiary')
    ),
  };
};

const mapLockedCities = (user = {}) => {
  const addresses = user.addresses || [];
  const locked = {};
  const labels = {};

  ADDRESS_SLOTS.forEach(({ key }) => {
    const address = getAddressByType(addresses, key);
    const cityId = resolveAddressCityId(address);
    locked[key] = Boolean(cityId);
    labels[key] = cityId
      ? formatCityName(address) === '--'
        ? formatCityOptionLabel(address?.city) || cityId
        : formatCityName(address)
      : '';
  });

  return { locked, labels };
};

const profileImageUrl = (user = {}) => getUserAvatarUrl(user);

/** Optional phone: digits + common separators; 7–15 digits (E.164). */
const isValidPhoneNumber = value => {
  const raw = String(value || '').trim()
  if (!raw) return true
  if (!/^\+?[\d\s().-]+$/.test(raw)) return false
  const digits = raw.replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15
}

const profileValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: yup.string().trim(),
  phone: yup
    .string()
    .trim()
    .test(
      'phone-format',
      'Enter a valid phone number (7–15 digits)',
      isValidPhoneNumber
    ),
  listeningTo: yup
    .string()
    .trim()
    .max(255, 'Listening to must be less than 255 characters'),
  reading: yup
    .string()
    .trim()
    .max(255, 'Reading must be less than 255 characters'),
  primaryCityId: yup.string().trim(),
  secondaryCityId: yup.string().trim(),
  tertiaryCityId: yup.string().trim(),
});

const passwordValidationSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS)
    .notOneOf(
      [yup.ref('currentPassword')],
      'New password must be different from current password'
    ),
  confirmPassword: yup
    .string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const profileInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  listeningTo: '',
  reading: '',
  primaryCityId: '',
  secondaryCityId: '',
  tertiaryCityId: '',
};

const passwordInitialValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

/* Password requirements: muted until typing, then red/green. */
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const handlePasswordSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await post(endpoints.updatePassword, {
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
        old_password: values.currentPassword,
        logout_other_devices: true,
      });

      if (response?.data?.success) {
        toast.success(
          response.data.message || 'Password updated successfully.',
          TOAST_OPTIONS
        );

        markUnloadHandled();
        const active = getActiveSessionSnapshot();
        await performAppLogout({
          notifyServer: true,
          pauseActive: active.sessionId
            ? () =>
                pauseActiveSession({
                  sessionId: active.sessionId,
                  shouldPause: active.shouldPause,
                  reason: TIMELOG_REASONS.logout,
                })
            : null,
        });
        navigate('/login', { replace: true });
        return;
      }

      if (response) {
        toastApiError(response, 'Failed to update password');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    centered
    className="amok-modal amok-modal--password"
    contentClassName="amok-modal__content"
    backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
  >
    <ModalBody className="amok-modal__body edit-profile__modal-body">
      <Formik
        key={isOpen ? 'password-open' : 'password-closed'}
        initialValues={passwordInitialValues}
        validationSchema={passwordValidationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={handlePasswordSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldTouched,
          isSubmitting,
        }) => (
            <Form className="edit-profile__modal-form">
              <div className="edit-profile__modal-main">
                <h3 className="edit-profile__modal-title">Change Password</h3>
                <div className="edit-profile__modal-fields">
                  <CustomInput
                    label="Current Password"
                    type="password"
                    name="currentPassword"
                    placeholder="Enter current password"
                    value={values.currentPassword}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('currentPassword', true)}
                    error={touched.currentPassword && errors.currentPassword}
                    autoComplete="current-password"
                  />
                  <div className="edit-profile__password-field">
                    <CustomInput
                      label="Create New Password"
                      type="password"
                      name="newPassword"
                      placeholder="Enter new password"
                      value={values.newPassword}
                      onChange={event => {
                        handleChange(event);
                        setFieldTouched('newPassword', true, false);
                      }}
                      onBlur={() => setFieldTouched('newPassword', true)}
                      error={getPasswordFieldError(
                        touched.newPassword,
                        errors.newPassword
                      )}
                      helperTone={getPasswordHelperTone(values.newPassword)}
                      autoComplete="new-password"
                    />
                    <PasswordRequirementsHint value={values.newPassword} />
                  </div>
                  <CustomInput
                    label="Confirm New Password"
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('confirmPassword', true)}
                    error={touched.confirmPassword && errors.confirmPassword}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="edit-profile__modal-actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  className="edit-profile__modal-cancel"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  className="edit-profile__modal-submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Update Password
                </CustomButton>
              </div>
            </Form>
          )}
      </Formik>
    </ModalBody>
  </Modal>
  );
};

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profileId } = useParams();
  const currentUser = useSelector(state => state.auth.currentUser);
  const fileInputRef = useRef(null);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formInitialValues, setFormInitialValues] = useState(profileInitialValues);
  const [lockedCities, setLockedCities] = useState({
    primary: false,
    secondary: false,
    tertiary: false,
  });
  const [lockedCityLabels, setLockedCityLabels] = useState({
    primary: '',
    secondary: '',
    tertiary: '',
  });
  const [activeTab, setActiveTab] = useState('profile');
  const [notificationPrefs, setNotificationPrefs] = useState(() =>
    buildDefaultNotificationPrefs(NOTIFICATION_SECTIONS),
  );
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [savingNotification, setSavingNotification] = useState(false);

  const loadNotificationSettings = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const response = await get(endpoints.getNotificationSettings);
      if (response?.data?.success) {
        setNotificationPrefs(
          mapNotificationSettingsFromApi(response.data.result || {}),
        );
      } else if (response) {
        toastApiError(response, 'Failed to load notification settings');
      }
    } catch (err) {
      toastApiError(err, 'Failed to load notification settings');
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (activeTab === 'notifications') {
      setLoadingNotifications(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadNotificationSettings();
    }
  }, [activeTab, loadNotificationSettings]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      const meRes = await get(endpoints.getMe, {
        params: { with: 'addresses.city' },
      });

      if (cancelled) return;

      if (meRes?.data?.success) {
        const user = meRes.data.result || {};
        dispatch(setUser(user));
        setFormInitialValues(mapUserToForm(user));
        const { locked, labels } = mapLockedCities(user);
        setLockedCities(locked);
        setLockedCityLabels(labels);
        setPhotoPreview(profileImageUrl(user));
      } else if (meRes) {
        toastApiError(meRes, 'Failed to load profile');
      }

      setLoading(false);
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const handlePhotoChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isAcceptedPhoto(file)) {
      setPhotoError('Invalid file type. Please upload a JPG, GIF, or PNG.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError('Photo must be 5MB or smaller.');
      e.target.value = '';
      return;
    }

    setPhotoError('');
    if (photoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    setProfileImageFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSubmit = async (values) => {
    if (saving) return;
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append(
        'first_name',
        formatDisplayName(values.firstName.trim()),
      );
      formData.append(
        'last_name',
        formatDisplayName(values.lastName?.trim() || ''),
      );
      formData.append('listening_to', values.listeningTo?.trim() || '');
      formData.append('reading', values.reading?.trim() || '');
      if (values.phone?.trim()) {
        formData.append('phone_number', values.phone.trim());
      }
      if (profileImageFile) {
        formData.append('profile_image', profileImageFile);
      }

      // Only send address slots that were empty on load (locked like email after set).
      ADDRESS_SLOTS.forEach(({ key, field }) => {
        if (lockedCities[key]) return;
        const cityId = String(values[field] || '').trim();
        if (cityId) {
          formData.append(`addresses[${key}]`, cityId);
        }
      });

      const response = await post(endpoints.updateBasicProfile, formData);

      if (response?.data?.success) {
        if (response.data.result) {
          dispatch(setUser(response.data.result));
        }
        toast.success(
          response.data.message || 'Profile updated successfully.',
          TOAST_OPTIONS
        );
        navigate(`/profile/${response.data.result?.id || currentUser?.id}`);
      } else if (response) {
        toastApiError(response, 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = async (key, enabled) => {
    if (savingNotification || loadingNotifications) return;

    const previousPrefs = notificationPrefs;
    const nextPrefs = { ...previousPrefs, [key]: enabled };
    setNotificationPrefs(nextPrefs);
    setSavingNotification(true);

    try {
      const response = await patch(
        endpoints.updateNotificationSettings,
        mapNotificationSettingsToApi(nextPrefs),
      );

      if (response?.data?.success) {
        const result = response.data.result;
        if (result && typeof result === 'object') {
          setNotificationPrefs(mapNotificationSettingsFromApi(result));
        }
        toast.success(
          'Notification settings updated successfully.',
          TOAST_OPTIONS,
        );
      } else if (response) {
        setNotificationPrefs(previousPrefs);
        toastApiError(response, 'Failed to update notification settings');
      }
    } catch (err) {
      setNotificationPrefs(previousPrefs);
      toastApiError(err, 'Failed to update notification settings');
    } finally {
      setSavingNotification(false);
    }
  };

  document.title = 'Edit Profile | AMOK . EARTH';

  if (profileId && !currentUser?.id) {
    return <Navigate to={`/public/profile/${profileId}`} replace />;
  }

  if (
    profileId &&
    currentUser?.id &&
    String(profileId) !== String(currentUser.id)
  ) {
    return <Navigate to={`/public/profile/${profileId}`} replace />;
  }

  return (
    <div className="page-content">
      <div className="edit-profile" data-profile-id={profileId}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={profileValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            setFieldValue,
            isSubmitting,
          }) => (
            <>
              <PageHeader
                className="edit-profile__header"
                title="Edit Profile"
                subtitle={
                  activeTab === 'notifications'
                    ? 'Manage how you receive notifications.'
                    : 'Update your profile.'
                }
              />

              <div className="edit-profile__toolbar">
                <ListingTabs
                  items={EDIT_PROFILE_TABS}
                  activeKey={activeTab}
                  onChange={setActiveTab}
                />
                {activeTab === 'profile' ? (
                  <div className="edit-profile__actions">
                    <CustomButton
                      variant="secondary"
                      size="md"
                      type="button"
                      className="edit-profile__btn-password"
                      onClick={() => setIsPasswordOpen(true)}
                    >
                      Change Password
                    </CustomButton>
                    <CustomButton
                      variant="primary"
                      size="md"
                      type="submit"
                      form={PROFILE_FORM_ID}
                      trailingIconSrc={ICONS.arrowRight}
                      trailingIconAlt=""
                      loading={saving || isSubmitting || loading}
                      disabled={saving || isSubmitting || loading}
                    >
                      Save Changes
                    </CustomButton>
                  </div>
                ) : null}
              </div>

              {activeTab === 'notifications' ? (
                <NotificationsTab
                  preferences={notificationPrefs}
                  onToggle={handleNotificationToggle}
                  sections={NOTIFICATION_SECTIONS}
                  isLoading={loadingNotifications}
                  disabled={savingNotification || loadingNotifications}
                />
              ) : (
              <Form id={PROFILE_FORM_ID} className="edit-profile__content">
                <section className="edit-profile__section">
                  <div className="edit-profile__section-head">
                    <h2 className="edit-profile__section-title">Profile Photo</h2>
                  </div>
                  <div className="edit-profile__photo-row">
                    <ProfilePhoto
                      src={photoPreview}
                      name={`${formInitialValues.firstName} ${formInitialValues.lastName}`.trim()}
                      loading={loading && !photoPreview}
                      className="edit-profile__photo"
                    />
                    <div className="edit-profile__photo-actions">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                        className="edit-profile__file-input"
                        onChange={handlePhotoChange}
                      />
                      <CustomButton
                        variant="ghost"
                        size="sm"
                        type="button"
                        className="edit-profile__upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Upload Photo
                      </CustomButton>
                      <p className="edit-profile__photo-hint">
                        JPG, GIF or PNG. Max size of 5MB
                      </p>
                      {photoError ? (
                        <p className="amok-field__message amok-field__message--error">
                          {photoError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </section>

                <section className="edit-profile__section edit-profile__section--info">
                  <div className="edit-profile__section-head">
                    <h2 className="edit-profile__section-title">
                      Personal Information
                    </h2>
                  </div>

                  <div className="edit-profile__guideline" role="note">
                    <span
                      className="edit-profile__guideline-icon"
                      aria-hidden="true"
                    >
                      <img
                        src={ICONS.callInfoCircle}
                        alt=""
                        width={24}
                        height={24}
                      />
                    </span>
                    <p className="edit-profile__guideline-text">
                      Your email and phone aren&apos;t shown on your public
                      profile. Others can still find you by email when inviting
                      collaborators.
                    </p>
                  </div>

                  <div className="edit-profile__form">
                    <div className="edit-profile__form-row">
                      <CustomInput
                        label="First Name"
                        name="firstName"
                        placeholder="Enter your first name"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched('firstName', true)}
                        error={touched.firstName && errors.firstName}
                        autoComplete="given-name"
                      />
                      <CustomInput
                        label="Last Name"
                        name="lastName"
                        placeholder="Enter your last name"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched('lastName', true)}
                        error={touched.lastName && errors.lastName}
                        autoComplete="family-name"
                      />
                    </div>

                    <div className="edit-profile__form-row">
                      <CustomInput
                        label="Email Address"
                        type="email"
                        name="email"
                        placeholder="Enter your email address"
                        value={values.email}
                        disabled
                        readOnly
                        autoComplete="email"
                      />
                      <CustomInput
                        label="Phone Number"
                        type="tel"
                        name="phone"
                        placeholder="Enter your phone number"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched('phone', true)}
                        error={touched.phone && errors.phone}
                        autoComplete="tel"
                      />
                    </div>

                    <div className="edit-profile__form-row edit-profile__form-row--3">
                      {ADDRESS_SLOTS.map(({ key, field, label }) =>
                        lockedCities[key] ? (
                          <CustomInput
                            key={field}
                            label={label}
                            name={field}
                            placeholder="City, State"
                            value={lockedCityLabels[key] || ''}
                            disabled
                            readOnly
                          />
                        ) : (
                          <PreferredCitySelect
                            key={field}
                            label={label}
                            loadFromApi
                            placeholder="Select a city"
                            excludeIds={getExcludeCityIdsForSlot(values, field)}
                            value={values[field]}
                            onChange={value => setFieldValue(field, value)}
                            onBlur={() => setFieldTouched(field, true)}
                            error={touched[field] && errors[field]}
                          />
                        )
                      )}
                    </div>

                    <CustomInput
                      label="Listening to"
                      name="listeningTo"
                      textarea
                      rows={3}
                      placeholder="What are you listening to? (optional)"
                      value={values.listeningTo}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('listeningTo', true)}
                      error={touched.listeningTo && errors.listeningTo}
                    />

                    <CustomInput
                      label="Reading"
                      name="reading"
                      textarea
                      rows={3}
                      placeholder="What are you reading? (optional)"
                      value={values.reading}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched('reading', true)}
                      error={touched.reading && errors.reading}
                    />
                  </div>
                </section>
              </Form>
              )}
            </>
          )}
        </Formik>

        <ChangePasswordModal
          isOpen={isPasswordOpen}
          onClose={() => setIsPasswordOpen(false)}
        />
      </div>
    </div>
  );
};

export default EditProfile;
